






















































































































































import { Component, Prop, Mixins, Vue, Inject } from "vue-property-decorator";
import { mapState } from "vuex";

// Mixins
import Context from "@/mixins/context";
import RequestPromises from "@/mixins/requestPromises";

// Data/Enums
import { DataModules } from "@/store/modules/data";

// Types
import { NewLine } from "@/store/modules/ui";
import { IState } from "@/store";
import {
  ITicketFeedProviderMethods,
  ITicketProvider,
  ITicketProviderMethods,
  ITicketFeedProvider
} from "@/models/providers/ticket";
import { TicketSourceType } from "@/data/enums/tickets";

@Component({
  computed: {
    ...mapState({
      newLine: (state: IState) => state.ui.support.newLine,
      submitWithShortcut: (state: IState) => state.ui.support.submitWithShortcut
    })
  }
})
export default class TicketMessage extends Mixins(RequestPromises, Context) {
  public $refs!: {
    textarea: Vue;
    cancelButton: HTMLButtonElement;
  };

  // Inject
  @Inject({ from: "ticketProvider" })
  public readonly $ticketData!: () => ITicketProvider;

  @Inject({ from: "ticketProviderMethods" })
  public readonly $ticketMethods!: ITicketProviderMethods;

  @Inject({ from: "ticketFeedProviderMethods" })
  public readonly $ticketFeedMethods!: ITicketFeedProviderMethods;

  @Inject({ from: "ticketFeedProviderData" })
  public readonly $ticketFeedData!: () => ITicketFeedProvider;

  // Props
  @Prop({ required: true })
  public message!: any;
  @Prop({ default: () => [] })
  public files!: any[];

  public newLine!: NewLine;
  public submitWithShortcut!: boolean;
  public isEditing = false;
  public isProcessing = false;
  public editedMessageBody = "";
  public focusOnCancel: any = null;

  public created() {
    this.$bus.$on(`ticket/edit-message-${this.message.id}`, data => {
      this.focusOnCancel = data.prevFocusedEl;
      this.editMessage(data.message);
    });
  }

  public beforeDestroy() {
    this.$bus.$off(`ticket/edit-message-${this.message.id}`);
  }

  // Getters
  public get ticketData() {
    return this.$ticketData();
  }

  public get editedMessageBodySanitised() {
    return this.$_.trim(this.editedMessageBody);
  }

  public get hasEmailSource() {
    return (
      this.message.email_file &&
      this.message.source_type === TicketSourceType.EMAIL
    );
  }

  public get ticketMessageDate() {
    const sourceIP = this.message.source_ip;
    if (this.isClient) return "_.ticket_message_date.default";
    if (this.hasEmailSource) return "_.ticket_message_date.email";
    if (sourceIP) return "_.ticket_message_date.ip";
    return "_.ticket_message_date.default";
  }

  public get relativeTimeKey() {
    const msgTime = new Date(this.message.timestamp).getTime();
    const nowTime = this.$store.state.utils.time.now.getTime();
    return [msgTime, nowTime].join("-");
  }

  public get editedTooltip() {
    const i18nPath = this.message.isInternal
      ? "_sentence.ticket.note_edited_tooltip"
      : "_sentence.ticket.message_edited_tooltip";
    return this.$t(i18nPath, {
      actor: this.message.actorName,
      date: this.$moment
        .utc(this.message.dateUpdated)
        .format(this.$t("_datetime.datetime.middle") as string)
    });
  }

  // Methods
  public openDeletedMessage() {
    this.$store.dispatch(
      `data/${DataModules.TICKETS_MESSAGES}/openDeletedMessageModal`,
      {
        props: {
          message: this.message
        }
      }
    );
  }

  public editMessage(message) {
    if (!this.message.isEditable) return;
    this.isEditing = true;
    this.editedMessageBody = message.body;
    this.$nextTick(() => {
      this.$refs.cancelButton.focus();
      // @ts-ignore
      this.$refs.textarea.$el.focus();
    });
  }

  public deleteMessage(message) {
    this.$ticketMethods
      .openDeleteMessageModal(message)
      .then(async ({ ticketMessage }) => {
        this.$ticketFeedMethods.removeMessage(message.id);
        if (this.isAdmin) {
          this.$ticketFeedMethods.addMessage(ticketMessage);
        }
      });
  }

  public cancelEdit() {
    this.isEditing = false;
    if (this.$_.isFunction(this.$_.get(this.focusOnCancel, "focus", null))) {
      this.focusOnCancel.focus();
      this.focusOnCancel = null;
    }
  }

  public updateMessage() {
    if (
      !this.isEditing ||
      this.isProcessing ||
      this.$_.isEmpty(this.editedMessageBodySanitised)
    ) {
      return;
    }
    this.isProcessing = true;
    this.$store
      .dispatch(`data/${DataModules.TICKETS}/replyUpdate`, {
        id: this.ticketData.ticketId,
        replyId: this.message.id,
        data: {
          body: this.editedMessageBodySanitised,
          is_private: this.message.is_private
        },
        vm: this
      })
      .then(message => {
        this.cancelEdit();
        this.$ticketFeedMethods.updateMessage(message);
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => (this.isProcessing = false));
  }

  public addNewLine() {
    this.editedMessageBody += "\n";
  }

  public onEnter() {
    if (this.newLine === NewLine.ENTER) {
      this.addNewLine();
      return;
    }
    if (this.submitWithShortcut) this.updateMessage();
  }

  public onShiftAndEnter() {
    if (this.newLine === NewLine.SHIFT_AND_ENTER) {
      this.addNewLine();
      return;
    }
    if (this.submitWithShortcut) this.updateMessage();
  }
}
